/** Mirrors backend `mvmConstants` / `mvmBreadSupply` for client-side estimates (alliance march modifiers may differ slightly). */

export const MVM_MS_PER_HEX = 30 * 1000
export const MVM_MIN_MARCH_MS = 15_000
export const MVM_BREAD_PER_CHAR_PER_HOUR_MARCH = 2
/** Attacking champions: bread per castle hex battle round (defenders use {@link MVM_BREAD_DEFENDER_PER_CHAR_PER_BATTLE_ROUND}). */
export const MVM_BREAD_PER_CHAR_PER_BATTLE_ROUND = 4
export const MVM_BREAD_DEFENDER_PER_CHAR_PER_BATTLE_ROUND = 2
export const MVM_BREAD_PLANNING_BATTLE_ROUNDS = 10

export function hexStepWorldMap(q1: number, r1: number, q2: number, r2: number): number {
  const dq = Math.abs((Number(q1) || 0) - (Number(q2) || 0))
  const dr = Math.abs((Number(r1) || 0) - (Number(r2) || 0))
  return Math.max(1, dq + dr)
}

/** Raw march duration before alliance speed bonuses (server applies `getWorldMapMarchTimeMsAfterAlliance`). */
export function estimateMarchMsRaw(fromQ: number, fromR: number, toQ: number, toR: number): number {
  const steps = hexStepWorldMap(fromQ, fromR, toQ, toR)
  return Math.max(MVM_MIN_MARCH_MS, Math.max(1, steps) * MVM_MS_PER_HEX)
}

export function mvmMarchBreadHourBuckets(marchTimeMs: number): number {
  const ms = Math.max(0, Number(marchTimeMs) || 0)
  if (ms <= 0) return 0
  return Math.max(1, Math.ceil(ms / 3600000))
}

export function mvmMarchBreadLoaves(characterCount: number, marchTimeMs: number): number {
  const n = Math.max(0, Math.floor(Number(characterCount) || 0))
  if (n === 0) return 0
  const h = mvmMarchBreadHourBuckets(marchTimeMs)
  return h * MVM_BREAD_PER_CHAR_PER_HOUR_MARCH * n
}

export function mvmMinCastleAttackerBreadLoaves(characterCount: number, marchTimeMs: number): number {
  const march = mvmMarchBreadLoaves(characterCount, marchTimeMs)
  const n = Math.max(0, Math.floor(Number(characterCount) || 0))
  const battlePlan = MVM_BREAD_PER_CHAR_PER_BATTLE_ROUND * MVM_BREAD_PLANNING_BATTLE_ROUNDS * n
  return march + battlePlan
}

/** Minimum bread a defender ally reinforcement carries: march supply + 10 rounds at the DEFENDER rate. */
export function mvmMinCastleDefenderBreadLoaves(characterCount: number, marchTimeMs: number): number {
  const march = mvmMarchBreadLoaves(characterCount, marchTimeMs)
  const n = Math.max(0, Math.floor(Number(characterCount) || 0))
  const battlePlan = MVM_BREAD_DEFENDER_PER_CHAR_PER_BATTLE_ROUND * MVM_BREAD_PLANNING_BATTLE_ROUNDS * n
  return march + battlePlan
}

export function mvmMarchOnlyBreadLoaves(characterCount: number, marchTimeMs: number): number {
  return mvmMarchBreadLoaves(characterCount, marchTimeMs)
}

export type MvmBreadSupplyContext =
  | { kind: 'castle_attack'; marchTimeMs: number }
  | { kind: 'tower_march'; marchTimeMs: number }
  /** Barbarian village / neutral defense tower: march bread only (auto-battle resolves at destination). */
  | { kind: 'pve_march'; marchTimeMs: number }
  /** Joiner must carry at least `leaderMinBreadLoaves` (matches rally leader's castle minimum). */
  | { kind: 'rally_join'; marchTimeMs: number; leaderMinBreadLoaves: number }
  /** Defender ally reinforcement: march supply + 10 defense rounds (defender per-round rate). */
  | { kind: 'reinforce'; marchTimeMs: number }
  | { kind: 'defender_choice' }
